import type { CorrelationIdType } from '@/types/correlation';
import type { SeverityLevel } from '@sentry/nextjs';

import { isStaleReviewInstallationError } from './review-errors';
import { captureException } from './sentry';

export type PrErrorContext = {
  type: string;
  owner: string;
  repo: string;
  pr: string;
  correlationId?: string;
  correlationIdType?: CorrelationIdType;
  extra?: Record<string, any>;
  level?: SeverityLevel;
};

export type SystemErrorContext = {
  type: string;
  correlationId?: string;
  correlationIdType?: CorrelationIdType;
  extra?: Record<string, any>;
};

/**
 * Track PR-related errors in Sentry with consistent context
 */
export function trackPrError(error: unknown, context: PrErrorContext) {
  if (isStaleReviewInstallationError(error)) {
    return;
  }

  const {
    type,
    owner,
    repo,
    pr,
    correlationId,
    correlationIdType,
    extra,
    level = 'error',
  } = context;
  captureException(error instanceof Error ? error : new Error(`${type} failed`), {
    tags: {
      type,
      owner,
      repo,
      pr,
      ...(correlationIdType && { correlationIdType }),
    },
    level,
    extra: {
      message: `${type} failed`,
      ...(correlationId && { correlationId }),
      ...(correlationIdType && { correlationIdType }),
      ...extra,
    },
  });
}

/**
 * Track system errors in Sentry that aren't tied to a specific PR.
 * Essentially a catchall for errors that don't have a specific function to catch errors.
 */
export function trackSystemError(error: unknown, context: SystemErrorContext) {
  const { type, correlationId, correlationIdType, extra } = context;
  captureException(error instanceof Error ? error : new Error(`${type} failed`), {
    tags: {
      type,
      ...(correlationId && { correlationId }),
      ...(correlationIdType && { correlationIdType }),
    },
    level: 'error',
    extra: {
      message: `${type} failed`,
      ...(correlationId && { correlationId }),
      ...(correlationIdType && { correlationIdType }),
      ...extra,
    },
  });
}

export type RepoErrorContext = {
  type: string;
  owner: string;
  repo: string;
  correlationId?: string;
  correlationIdType?: CorrelationIdType;
  extra?: Record<string, any>;
};

/**
 * Track repository-related errors in Sentry with consistent context
 * Used for operations that don't involve PRs (e.g. member management, repo syncs, webhooks)
 */
export function trackRepoError(error: unknown, context: RepoErrorContext) {
  const { type, owner, repo, correlationId, correlationIdType, extra } = context;
  captureException(error instanceof Error ? error : new Error(`${type} failed`), {
    tags: {
      type,
      owner,
      repo,
      ...(correlationId && { correlationId }),
      ...(correlationIdType && { correlationIdType }),
    },
    level: 'error',
    extra: {
      message: `${type} failed`,
      ...(correlationId && { correlationId }),
      ...(correlationIdType && { correlationIdType }),
      ...extra,
    },
  });
}
